import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { TableColumnsType } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError } from '../api/errors';
import {
  UnitOfMeasureType,
  createUnitOfMeasure,
  deleteUnitOfMeasure,
  listUnitsOfMeasure,
  setUnitOfMeasureActive,
  unitOfMeasureTypeNames,
  updateUnitOfMeasure,
} from '../api/unitsOfMeasure';
import type {
  UnitOfMeasure,
  UnitOfMeasureTypeValue,
} from '../api/unitsOfMeasure';
import { PermissionCodes } from '../auth/permissions';
import { useAuth } from '../auth/useAuth';

/** Giá trị của biểu mẫu thêm hoặc sửa đơn vị đo. */
interface UnitOfMeasureFormValues {
  readonly code: string;
  readonly name: string;
  readonly nameEn: string;
  readonly unitOfMeasureType: UnitOfMeasureTypeValue;
  readonly decimalPlaces: number;
  readonly baseUnitId: string | null;
  readonly sortOrder: number;
}

/** Dữ liệu của một trang đã nạp xong, kèm khóa nhận dạng lần nạp đó. */
interface LoadedPage {
  readonly key: string;
  readonly items: readonly UnitOfMeasure[];
  readonly totalCount: number;
}

/** Số bản ghi mỗi trang. Khớp với mặc định của máy chủ. */
const pageSize = 25;

/** Các lựa chọn của ô chọn loại đơn vị. */
const typeOptions = Object.entries(unitOfMeasureTypeNames).map(([value, label]) => ({
  value: Number(value) as UnitOfMeasureTypeValue,
  label,
}));

/**
 * Trang danh mục đơn vị đo.
 *
 * Đây là màn hình danh mục đầu tiên của hệ thống, nên nó cố ý dựng theo khuôn sẽ dùng lại cho mọi
 * danh mục sau: thanh công cụ có ô tìm kiếm và bộ lọc ở trên, bảng dữ liệu phân trang ở giữa, biểu mẫu
 * thêm và sửa trong hộp thoại.
 *
 * Ba điểm về cách nó hoạt động:
 *
 * 1. **Nút bấm ẩn hiện theo quyền, nhưng đó không phải lớp bảo vệ.** Máy chủ kiểm tra lại quyền ở
 *    từng điểm cuối. Ẩn nút chỉ để người dùng không bấm vào thứ họ không làm được.
 * 2. **Đơn vị do hệ thống định nghĩa không sửa và không xóa được.** Giao diện vô hiệu hóa nút và nói
 *    rõ lý do, thay vì để người dùng bấm rồi nhận thông báo từ chối.
 * 3. **Quy tắc nhập liệu do máy chủ quyết định.** Giao diện chỉ chặn ô trống và hiển thị nguyên văn
 *    lý do máy chủ trả về. Nhân bản danh sách quy tắc ở hai nơi thì hai bản sẽ lệch nhau.
 */
export function UnitOfMeasurePage(): ReactNode {
  const { hasPermission } = useAuth();

  const canManage = hasPermission(PermissionCodes.UnitOfMeasureManage);

  const [form] = Form.useForm<UnitOfMeasureFormValues>();

  const [pageNumber, setPageNumber] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<UnitOfMeasureTypeValue | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);

  /**
   * Tăng lên mỗi lần người dùng bấm Tải lại, hoặc sau mỗi lần lưu.
   *
   * Cần thiết vì điều kiện lọc không đổi mà dữ liệu thì đã đổi — nếu chỉ dựa vào điều kiện lọc để
   * quyết định có nạp lại hay không thì sau khi lưu, bảng vẫn hiển thị dữ liệu cũ.
   */
  const [reloadToken, setReloadToken] = useState(0);

  const [loaded, setLoaded] = useState<LoadedPage | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [editingUnit, setEditingUnit] = useState<UnitOfMeasure | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  /** Khóa nhận dạng một lần nạp. Đổi khóa nghĩa là cần nạp lại. */
  const requestKey = useMemo(
    () =>
      JSON.stringify({
        searchTerm,
        typeFilter,
        includeInactive,
        pageNumber,
        reloadToken,
      }),
    [searchTerm, typeFilter, includeInactive, pageNumber, reloadToken],
  );

  /*
    Trạng thái đang tải được SUY RA, không giữ thành một biến trạng thái riêng.

    Lý do không chỉ là gọn: đặt một biến trạng thái ngay trong effect làm React dựng lại giao diện
    hai lần cho mỗi lần nạp, và bộ kiểm tra mã nguồn chặn hẳn cách viết đó. Suy ra bằng cách so khóa
    của lần nạp đang cần với khóa của dữ liệu đang có thì không cần đặt trạng thái nào trước khi chờ.
  */
  const isLoading = loaded?.key !== requestKey && errorMessage === null;

  const reload = useCallback((): void => {
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        const page = await listUnitsOfMeasure({
          searchTerm,
          ...(typeFilter === null ? {} : { unitOfMeasureType: typeFilter }),
          includeInactive,
          pageNumber,
          pageSize,
        });

        if (controller.signal.aborted) {
          return;
        }

        setErrorMessage(null);
        setLoaded({ key: requestKey, items: page.items, totalCount: page.totalCount });
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        setErrorMessage(
          error instanceof ApiError
            ? error.message
            : 'Không kết nối được tới máy chủ. Kiểm tra lại đường mạng.',
        );
      }
    })();

    return () => {
      controller.abort();
    };
  }, [requestKey, searchTerm, typeFilter, includeInactive, pageNumber]);

  /*
    Bọc trong useMemo thay vì viết trực tiếp: một biểu thức trả về mảng rỗng mới ở mỗi lần dựng sẽ
    làm mọi phép ghi nhớ phụ thuộc vào nó tính lại vô ích ở mỗi lần dựng.
  */
  const items = useMemo<readonly UnitOfMeasure[]>(() => loaded?.items ?? [], [loaded]);

  const totalCount = loaded?.totalCount ?? 0;

  /** Các đơn vị dùng được làm đơn vị gốc cho loại đang chọn trong biểu mẫu. */
  const formType = Form.useWatch('unitOfMeasureType', form);

  const baseUnitOptions = useMemo(
    () =>
      items
        .filter(
          (unit) =>
            unit.baseUnitId === null
            && unit.unitOfMeasureType === formType
            && unit.id !== editingUnit?.id,
        )
        .map((unit) => ({ value: unit.id, label: `${unit.code} — ${unit.name}` })),
    [items, formType, editingUnit],
  );

  function openCreateForm(): void {
    setEditingUnit(null);
    form.setFieldsValue({
      code: '',
      name: '',
      nameEn: '',
      unitOfMeasureType: UnitOfMeasureType.Count,
      decimalPlaces: 0,
      baseUnitId: null,
      sortOrder: 900,
    });
    setIsFormOpen(true);
  }

  function openEditForm(unit: UnitOfMeasure): void {
    setEditingUnit(unit);
    form.setFieldsValue({
      code: unit.code,
      name: unit.name,
      nameEn: unit.nameEn,
      unitOfMeasureType: unit.unitOfMeasureType,
      decimalPlaces: unit.decimalPlaces,
      baseUnitId: unit.baseUnitId,
      sortOrder: unit.sortOrder,
    });
    setIsFormOpen(true);
  }

  async function handleSubmit(values: UnitOfMeasureFormValues): Promise<void> {
    setIsSaving(true);

    try {
      if (editingUnit === null) {
        await createUnitOfMeasure({
          code: values.code,
          name: values.name,
          nameEn: values.nameEn,
          unitOfMeasureType: values.unitOfMeasureType,
          decimalPlaces: values.decimalPlaces,
          baseUnitId: values.baseUnitId,
          sortOrder: values.sortOrder,
        });

        void message.success('Đã thêm đơn vị đo.');
      } else {
        await updateUnitOfMeasure(editingUnit.id, {
          name: values.name,
          nameEn: values.nameEn,
          decimalPlaces: values.decimalPlaces,
          baseUnitId: values.baseUnitId,
          sortOrder: values.sortOrder,
        });

        void message.success('Đã lưu thay đổi.');
      }

      setIsFormOpen(false);
      reload();
    } catch (error) {
      // Hiển thị nguyên văn lý do máy chủ trả về. Giao diện không đoán thêm: danh sách quy tắc
      // nằm ở máy chủ, và nhân bản nó ở đây sẽ dẫn tới hai bản lệch nhau.
      void message.error(
        error instanceof ApiError ? error.message : 'Không lưu được. Thử lại sau.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  async function handleToggleActive(unit: UnitOfMeasure): Promise<void> {
    try {
      await setUnitOfMeasureActive(unit.id, !unit.isActive);
      reload();
    } catch (error) {
      void message.error(
        error instanceof ApiError ? error.message : 'Không đổi được trạng thái.',
      );
    }
  }

  async function handleDelete(unit: UnitOfMeasure): Promise<void> {
    try {
      await deleteUnitOfMeasure(unit.id);
      void message.success('Đã xóa đơn vị đo.');
      reload();
    } catch (error) {
      void message.error(error instanceof ApiError ? error.message : 'Không xóa được.');
    }
  }

  const columns: TableColumnsType<UnitOfMeasure> = [
    {
      title: 'Mã',
      dataIndex: 'code',
      width: 110,
      render: (code: string) => <Typography.Text strong>{code}</Typography.Text>,
    },
    { title: 'Tên', dataIndex: 'name' },
    { title: 'Tên tiếng Anh', dataIndex: 'nameEn' },
    {
      title: 'Loại',
      dataIndex: 'unitOfMeasureType',
      width: 130,
      render: (type: UnitOfMeasureTypeValue) => unitOfMeasureTypeNames[type],
    },
    {
      title: 'Đơn vị gốc',
      dataIndex: 'baseUnitCode',
      width: 110,
      render: (baseUnitCode: string | null) => baseUnitCode ?? '—',
    },
    {
      title: 'Số lẻ',
      dataIndex: 'decimalPlaces',
      width: 80,
      align: 'right',
    },
    {
      title: 'Trạng thái',
      dataIndex: 'isActive',
      width: 150,
      render: (_isActive: boolean, unit: UnitOfMeasure) => (
        <Space size={4}>
          {unit.isActive ? (
            <Tag color="green">Đang dùng</Tag>
          ) : (
            <Tag>Đã ngừng</Tag>
          )}
          {unit.isSystemDefined && <Tag color="blue">Hệ thống</Tag>}
        </Space>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 170,
      render: (_value: unknown, unit: UnitOfMeasure) =>
        canManage && !unit.isSystemDefined ? (
          <Space size={4}>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                openEditForm(unit);
              }}
            >
              Sửa
            </Button>

            <Button
              size="small"
              onClick={() => {
                void handleToggleActive(unit);
              }}
            >
              {unit.isActive ? 'Ngừng' : 'Mở lại'}
            </Button>

            <Popconfirm
              title="Xóa đơn vị đo này?"
              description="Chứng từ cũ tham chiếu tới nó vẫn đọc được."
              okText="Xóa"
              cancelText="Không"
              onConfirm={() => {
                void handleDelete(unit);
              }}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ) : (
          <Typography.Text type="secondary">
            {unit.isSystemDefined ? 'Không sửa được' : ''}
          </Typography.Text>
        ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Typography.Title level={4} style={{ margin: 0 }}>
        Đơn vị đo
      </Typography.Title>

      {errorMessage !== null && <Alert type="error" showIcon message={errorMessage} />}

      <Space wrap>
        <Input.Search
          placeholder="Tìm theo mã hoặc tên"
          allowClear
          style={{ width: 260 }}
          onSearch={(value) => {
            setPageNumber(1);
            setSearchTerm(value);
          }}
        />

        <Select<UnitOfMeasureTypeValue | null>
          placeholder="Mọi loại"
          allowClear
          style={{ width: 180 }}
          value={typeFilter}
          options={typeOptions}
          onChange={(value) => {
            setPageNumber(1);
            setTypeFilter(value ?? null);
          }}
        />

        <Space size={4}>
          <Switch
            checked={includeInactive}
            onChange={(checked) => {
              setPageNumber(1);
              setIncludeInactive(checked);
            }}
          />
          <Typography.Text>Hiện cả đơn vị đã ngừng</Typography.Text>
        </Space>

        <Button
          icon={<ReloadOutlined />}
          onClick={reload}
        >
          Tải lại
        </Button>

        {canManage && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateForm}>
            Thêm đơn vị
          </Button>
        )}
      </Space>

      <Table<UnitOfMeasure>
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={[...items]}
        loading={isLoading}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: pageNumber,
          pageSize,
          total: totalCount,
          showSizeChanger: false,

          // Hiển thị tổng số: nó cho người dùng biết bộ lọc vừa đặt có đúng hay không, trước khi
          // họ bắt đầu đọc từng dòng.
          showTotal: (total, range) =>
            `${range[0].toLocaleString('vi-VN')}–${range[1].toLocaleString('vi-VN')}`
            + ` trong ${total.toLocaleString('vi-VN')}`,
          onChange: setPageNumber,
        }}
      />

      <Modal
        title={editingUnit === null ? 'Thêm đơn vị đo' : `Sửa đơn vị ${editingUnit.code}`}
        open={isFormOpen}
        confirmLoading={isSaving}
        onOk={() => {
          form.submit();
        }}
        onCancel={() => {
          setIsFormOpen(false);
        }}
        okText="Lưu"
        cancelText="Hủy"
        destroyOnHidden
      >
        <Form<UnitOfMeasureFormValues>
          form={form}
          layout="vertical"
          requiredMark={false}
          disabled={isSaving}
          onFinish={(values) => {
            void handleSubmit(values);
          }}
        >
          <Form.Item
            name="code"
            label="Mã đơn vị"
            extra="Chữ in hoa không dấu, chữ số, dấu gạch nối. Ví dụ: PAIR, ROLL-50."
            rules={[{ required: true, message: 'Chưa nhập mã đơn vị.' }]}
          >
            {/* Không sửa được mã: nó xuất hiện trên chứng từ đã in và trên tệp đã xuất. */}
            <Input disabled={editingUnit !== null} />
          </Form.Item>

          <Form.Item
            name="name"
            label="Tên tiếng Việt"
            rules={[{ required: true, message: 'Chưa nhập tên tiếng Việt.' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="nameEn"
            label="Tên tiếng Anh"
            extra="In trên đơn đặt hàng gửi hãng nước ngoài, nên không để trống."
            rules={[{ required: true, message: 'Chưa nhập tên tiếng Anh.' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="unitOfMeasureType"
            label="Loại đơn vị"
            extra="Chỉ đơn vị cùng loại mới quy đổi được cho nhau. Không sửa được sau khi tạo."
            rules={[{ required: true, message: 'Chưa chọn loại đơn vị.' }]}
          >
            <Select options={typeOptions} disabled={editingUnit !== null} />
          </Form.Item>

          <Form.Item
            name="baseUnitId"
            label="Đơn vị gốc"
            extra="Để trống nếu chính nó là đơn vị gốc. Chỉ chọn được đơn vị gốc cùng loại."
          >
            <Select allowClear options={baseUnitOptions} placeholder="Chính nó là gốc" />
          </Form.Item>

          <Form.Item
            name="decimalPlaces"
            label="Số chữ số thập phân"
            extra="Đặt 0 cho đơn vị đếm: không thể xuất kho 2,5 cái camera."
            rules={[{ required: true, message: 'Chưa nhập số chữ số thập phân.' }]}
          >
            <InputNumber min={0} max={6} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="sortOrder"
            label="Thứ tự trình bày"
            extra="Số nhỏ nằm trên. Dùng để đưa đơn vị hay dùng lên đầu danh sách chọn."
            rules={[{ required: true, message: 'Chưa nhập thứ tự trình bày.' }]}
          >
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
